/**
 * Tap to Pay service — wraps the @capacitor-community/stripe-terminal plugin.
 *
 * Architecture note:
 *   The Stripe Terminal SDK uses a token provider callback pattern. Rather than
 *   supplying `tokenProviderEndpoint` (which would make an unauthenticated fetch),
 *   we listen to the `RequestedConnectionToken` event and supply the token via
 *   `setConnectionToken`, calling our authenticated apiService so the bearer
 *   token and company headers are included automatically.
 */

import {
    StripeTerminal,
    TerminalEventsEnum,
    TerminalConnectTypes,
} from '@capacitor-community/stripe-terminal';
import { Device, type DeviceInfo } from '@capacitor/device';
import { App } from '@capacitor/app';
import { Geolocation } from '@capacitor/geolocation';
import type { PluginListenerHandle } from '@capacitor/core';
import {
    getStorageItem,
    isAndroid,
    isIOS,
    isNative,
    requestPermissionExclusive,
    setStorageItem,
} from './capacitor';
import { apiService } from './api';
import { ENV } from '@/config/environment';

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Extract a human-readable string from any thrown value.
 * Capacitor plugin errors may surface the message in different properties.
 */
function extractMessage(err: unknown): string {
    if (!err) return 'Unknown error';
    if (typeof err === 'string') return err;
    const e = err as any;
    return e.message || e.errorMessage || e.localizedMessage || JSON.stringify(e);
}

/**
 * Apple's approved product name (App Review checklist §5.5). It is "Tap to Pay on
 * iPhone" in full — never shortened — wherever the feature is named on iOS.
 * Android keeps the generic name; "Tap to Pay on iPhone" is an Apple trademark.
 */
export function tapToPayName(): string {
    return isIOS() ? 'Tap to Pay on iPhone' : 'Tap to Pay';
}

/**
 * Where the customer should hold the card. The contactless antenna sits at the
 * *top* of an iPhone (beside the rear camera) but on the *back* of most Android
 * handsets, so this cannot be one hardcoded string — it was iPhone-wrong before.
 */
export function cardPositionHint(): string {
    return isIOS() ? 'near the top of the iPhone' : 'near the back of the phone';
}

/** Value-proposition copy for the one-time Tap to Pay splash screen. */
export function tapToPaySplashDescription(): string {
    if (isIOS()) {
        return 'Take contactless cards, Apple Pay and other digital wallets right on your iPhone — '
            + 'no extra hardware. Your customer just holds their card or phone near the top of yours.';
    }
    return 'Take contactless cards, Google Pay and other digital wallets right on this phone — '
        + `no extra hardware. Your customer just holds their card or phone ${cardPositionHint()}.`;
}

/** Settings-card setup blurb (Payment provider page). */
export function tapToPaySetupDescription(): string {
    if (isIOS()) {
        return `Setting up accepts Apple's ${tapToPayName()} Terms & Conditions and prepares this `
            + "phone, so the first customer payment doesn't have to wait on the one-time screens.";
    }
    return `Setting up prepares this phone for ${tapToPayName()}, so the first customer payment `
        + "doesn't have to wait on the one-time screens.";
}

/** Inline setup prompt inside the Take Payment modal. */
export function tapToPaySetupPrompt(): string {
    if (isIOS()) {
        return `Setting up accepts Apple's ${tapToPayName()} Terms & Conditions and takes about a `
            + 'minute. Doing it now means no customer has to wait.';
    }
    return 'Setting up takes about a minute. Doing it now means no customer has to wait on the '
        + 'one-time screens.';
}

function formatOsVersionLabel(info: DeviceInfo | null): string {
    if (!info?.osVersion) {
        return isIOS() ? 'an unknown iOS version' : 'an unknown Android version';
    }
    return isIOS() ? `iOS ${info.osVersion}` : `Android ${info.osVersion}`;
}

function tapToPayInitializeTimeoutMessage(): string {
    if (isIOS()) {
        return 'Tap to Pay timed out while initializing. Check that Location permission is set to '
            + '"Precise" for this app (Settings → Privacy & Security → Location Services) and try again.';
    }
    return 'Tap to Pay timed out while initializing. Check that Location permission is set to '
        + '"Precise" for this app (Settings → Apps → Permissions → Location) and try again.';
}

function formatDeviceLabel(info: DeviceInfo): string {
    const parts = [info.manufacturer, info.model].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'this device';
}

function parseAndroidVersion(info: DeviceInfo): number | null {
    const osVersionNum = parseInt(info.osVersion, 10);
    return Number.isNaN(osVersionNum) ? null : osVersionNum;
}

// iOS reports osVersion like "16.7.1"; parse major.minor to compare against the
// Stripe Tap to Pay on iPhone minimum (iOS 16.7).
function parseIOSVersion(info: DeviceInfo): { major: number; minor: number } | null {
    const m = /^(\d+)(?:\.(\d+))?/.exec(info.osVersion ?? '');
    if (!m) return null;
    return { major: parseInt(m[1], 10), minor: m[2] ? parseInt(m[2], 10) : 0 };
}

async function getDeviceInfoSafe(): Promise<DeviceInfo | null> {
    try {
        return await Device.getInfo();
    } catch {
        return null;
    }
}

function getNoReaderMessage(info: DeviceInfo | null): string {
    const deviceLabel = info ? formatDeviceLabel(info) : 'This device';
    const versionText = formatOsVersionLabel(info);

    return [
        `Stripe could not discover ${deviceLabel} as a Tap to Pay reader (${versionText}).`,
    ].join(' ');
}

function createAttemptId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function summarizeDeviceInfo(info: DeviceInfo | null): Record<string, unknown> {
    if (!info) {
        return { available: false };
    }

    return {
        available: true,
        manufacturer: info.manufacturer || null,
        model: info.model || null,
        platform: info.platform || null,
        operatingSystem: info.operatingSystem || null,
        osVersion: info.osVersion || null,
        isVirtual: info.isVirtual,
    };
}

function summarizeReader(reader: any): Record<string, unknown> {
    if (!reader || typeof reader !== 'object') {
        return { available: false };
    }

    const summary: Record<string, unknown> = {};
    const candidateKeys = ['label', 'serialNumber', 'deviceType', 'id', 'simulated', 'status'];

    for (const key of candidateKeys) {
        const value = reader[key];
        if (value != null) {
            summary[key] = value;
        }
    }

    if (Object.keys(summary).length === 0) {
        summary.keys = Object.keys(reader).slice(0, 10);
    }

    return summary;
}

function sanitizeDiagnosticsValue(value: unknown): unknown {
    if (value == null || typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        if (value.includes('_secret_')) {
            return '[redacted-secret]';
        }

        return value.length > 300 ? `${value.slice(0, 300)}...` : value;
    }

    if (Array.isArray(value)) {
        return value.slice(0, 10).map(sanitizeDiagnosticsValue);
    }

    if (typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .slice(0, 20)
                .map(([key, entryValue]) => [key, sanitizeDiagnosticsValue(entryValue)])
        );
    }

    return String(value);
}

function stringifyDiagnosticsDetails(details?: Record<string, unknown>): string {
    if (!details || Object.keys(details).length === 0) {
        return '';
    }

    return ` ${JSON.stringify(sanitizeDiagnosticsValue(details))}`;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type TapToPayUnsupportedReason =
    | 'not_native'
    | 'unsupported_platform'
    | 'android_version'
    | 'ios_version'
    | 'virtual_device'
    | 'nfc_unavailable'
    | 'device_unsupported'
    | 'offline';

export type TapToPaySupportResult =
    | { supported: true }
    | { supported: false; reason: TapToPayUnsupportedReason; message: string };

export type TapToPayProgressStage =
    | 'initializing'
    | 'creating_payment'
    | 'resolving_location'
    | 'discovering_reader'
    | 'connecting_reader'
    | 'configuring_reader'
    | 'waiting_for_tap'
    | 'processing_payment'
    | 'confirming_payment';

export interface TapToPayProgressUpdate {
    stage: TapToPayProgressStage;
    message: string;
    source: 'local' | 'terminal';
    /**
     * 0..1 install progress, present only during 'configuring_reader'. Apple §3.6
     * wants a real configuration indicator; the first-run Tap to Pay provisioning
     * emits ReaderSoftwareUpdateProgress, which this carries so the UI can show a
     * determinate bar instead of a static "Connecting…".
     */
    progress?: number;
}

type TapToPayDiagnosticLevel = 'info' | 'warn' | 'error';

interface TapToPayDiagnosticEntry {
    attemptId: string;
    timestamp: string;
    level: TapToPayDiagnosticLevel;
    stage: string;
    message: string;
    details?: Record<string, unknown>;
}

// ── Module-level state ────────────────────────────────────────────────────────

/** Whether the Terminal SDK has been initialized this session */
let initialized = false;

/** In-flight init+discover+connect, shared by every concurrent caller. See ensureReaderWarm. */
let warmPromise: Promise<void> | null = null;

/** Set by the connect path: true when it reused an already-connected reader. */
let reusedExistingConnection = false;

/** Cached value of the persisted "a reader has connected on this device before" flag. */
let onboardedCache: boolean | null = null;

/** Active listener handle for the connection token event */
let tokenListenerHandle: PluginListenerHandle | null = null;

/** Last error from the connection token fetch — surfaced if readers come back empty */
let lastTokenError: string | null = null;
let tokenRequestAttemptId: string | null = null;
let tokenErrorAttemptId: string | null = null;

/**
 * Timestamp of the last RequestedConnectionToken event from the native SDK.
 * Used by the token watchdog to detect a stalled native bridge.
 */
let lastTokenRequestAt = 0;

/**
 * Timestamp of the last connection/payment status change from the native SDK.
 * Any progress here means the native connect is alive even if no connection
 * token was requested (cached session), so the connect watchdog disarms on it.
 */
let lastTerminalProgressAt = 0;

/**
 * App foreground state, used to keep the connect watchdog from counting time the
 * user spends in the first-run Apple Tap to Pay onboarding / Terms sheet (which
 * backgrounds the app). `lastAppResumeAt` restarts the watchdog window each time
 * the app returns to the foreground so onboarding never trips a false stall.
 */
let appIsActive = true;
let lastAppResumeAt = 0;
let appStateListenerHandle: PluginListenerHandle | null = null;

async function ensureAppStateListenerRegistered(): Promise<void> {
    if (appStateListenerHandle) return;
    appStateListenerHandle = await App.addListener('appStateChange', ({ isActive }) => {
        appIsActive = isActive;
        if (isActive) {
            lastAppResumeAt = Date.now();
            return;
        }
        // Going to the background is the last reliable chance to persist
        // buffered breadcrumbs before the OS can kill the process.
        void flushTapToPayDiagnostics();
    });
}

/** Cached device-level compatibility checks that do not change during the app session */
let cachedSupportResult: TapToPaySupportResult | null = null;

/** Additional listener handles used to persist native SDK event breadcrumbs */
let diagnosticListenerHandles: PluginListenerHandle[] = [];
let currentProgressListener: ((update: TapToPayProgressUpdate) => void) | null = null;

/** Persisted attempt breadcrumbs to inspect later without attaching a debugger */
let currentAttemptId: string | null = null;
let diagnosticsCache: TapToPayDiagnosticEntry[] | null = null;
let diagnosticsLoadPromise: Promise<TapToPayDiagnosticEntry[]> | null = null;
/** Entries appended since the last storage flush — see DIAGNOSTICS_FLUSH_DEBOUNCE_MS. */
let pendingDiagnostics: TapToPayDiagnosticEntry[] = [];
let diagnosticsFlushTimerId = 0;
let diagnosticsFlushPromise: Promise<void> | null = null;
/** `performance.now()` at the moment the user asked to pay; see markTapToPayIntent. */
let readyMarkAt: number | null = null;
let timingsCache: number[] | null = null;
let latestDiscoveredReadersFromEvent: any[] = [];
let latestDiscoveredReadersAt = 0;
let discoveredReaderWaiters: Array<(readers: any[]) => void> = [];

/**
 * Last `Failed` event from the native SDK during the current discovery window.
 * The native plugin emits a Failed event (with message + Stripe error code) for
 * discovery failures, but Capacitor does NOT reliably propagate the matching
 * call.reject to the discoverReaders() promise once a DISCOVERING status has
 * been delivered. So we capture the event here and consult it instead of waiting
 * out the full discovery timeout and showing a generic "no reader" message.
 */
let lastTerminalFailure: { message: string; code: string | null; declineCode: string | null } | null = null;
let terminalFailureWaiters: Array<() => void> = [];

/**
 * Local mirror of the native SDK's reader connection status.
 * Maintained by the ConnectionStatusChange / ConnectedReader / DisconnectedReader
 * listeners. Used to skip discover+connect when a reader is already connected
 * and to guard cleanupTerminal from issuing a no-op disconnect.
 */
type TerminalConnectionStatus = 'not_connected' | 'connecting' | 'connected';
let connectionStatus: TerminalConnectionStatus = 'not_connected';

const TAP_TO_PAY_DIAGNOSTICS_STORAGE_KEY = 'tap_to_pay_diagnostics_v1';
const TAP_TO_PAY_DIAGNOSTICS_MAX_ENTRIES = 200;
/**
 * Diagnostics are appended in memory and flushed to storage on a trailing
 * debounce. Writing on every entry meant a JSON.stringify of the whole ring plus
 * a Capacitor storage round trip for every native SDK event — dozens of bridge
 * hops sitting directly on the payment critical path. `error` entries still
 * flush immediately (see recordTapToPayDiagnostic).
 */
const DIAGNOSTICS_FLUSH_DEBOUNCE_MS = 1000;
/**
 * How far before an attempt's first entry the report reaches back, so background
 * warm breadcrumbs (written under the previous attempt id) are included.
 */
const DIAGNOSTICS_LEAD_UP_WINDOW_MS = 120000;
const TAP_TO_PAY_ONBOARDED_STORAGE_KEY = 'tap_to_pay_onboarded_v1';
const TAP_TO_PAY_TIMINGS_STORAGE_KEY = 'tap_to_pay_timings_v1';
const TAP_TO_PAY_TIMINGS_MAX_ENTRIES = 50;
/** Apple's Tap to Pay on iPhone guidance: ready to present a card within 1s, 90% of the time. */
const TAP_TO_PAY_READY_BUDGET_MS = 1000;
/**
 * Total time we are willing to wait for a non-empty reader list, either from
 * the discoverReaders() promise or from a DiscoveredReaders event. The native
 * plugin resolves the JS promise on the first onUpdateDiscoveredReaders
 * callback (often empty), so we treat the promise as a hint and rely on the
 * event for the actual reader list.
 */
const DISCOVERY_OVERALL_TIMEOUT_MS = 15000;
/**
 * Maximum time we wait inside connectReader for the native SDK to make any
 * observable progress (a RequestedConnectionToken request OR a connection /
 * payment status change). The previous 8s window produced false positives:
 * when the SDK already held a cached session token it never re-requested one,
 * and first-run attestation / Tap to Pay module provisioning on slow phones
 * legitimately takes longer than 8s before any token request. We now wait 60s
 * of *foreground* time (the watchdog is paused while the app is backgrounded by
 * the first-run Apple onboarding sheet), disarm on ANY progress event, then
 * reconcile with getConnectedReader() before declaring the attempt dead.
 */
const TOKEN_REQUEST_WATCHDOG_MS = 60000;
/**
 * Maximum time we wait for StripeTerminal.initialize() to settle. The patched
 * plugin now rejects (instead of looping) when location permission is denied,
 * but we keep a timeout as a backstop so the modal never hangs on
 * "Initializing…" indefinitely.
 */
const INITIALIZE_TIMEOUT_MS = 15000;
const SET_CONNECTION_TOKEN_RETRY_ATTEMPTS = 4;
const SET_CONNECTION_TOKEN_RETRY_DELAY_MS = 150;
/**
 * Upper bound for the "housekeeping" native calls made during cleanup/reconcile
 * (cancelDiscoverReaders / getConnectedReader / disconnectReader). After a FAILED
 * Tap to Pay discovery the native discovery Cancelable can be left in a state
 * where its cancel callback never fires, so an unbounded await there would hang
 * cleanupTerminal() — which the hook awaits BEFORE transitioning to the failed
 * state, freezing the UI on "Finding Tap to Pay reader…". These calls are
 * best-effort, so timing out is safe.
 */
const NATIVE_HOUSEKEEPING_TIMEOUT_MS = 2500;
const TOKEN_BRIDGE_STALLED_MESSAGE =
    'Connection token bridge stalled. Close and reopen the Tap to Pay screen to retry.';

function resetDiscoveredReadersEventState(): void {
    latestDiscoveredReadersFromEvent = [];
    latestDiscoveredReadersAt = 0;
    lastTerminalFailure = null;
}

/**
 * Record a native `Failed` event and wake anyone waiting on a discovery failure
 * (so the discovery wait can short-circuit instead of running the full timeout).
 */
function recordTerminalFailure(
    message: string | null,
    code: string | null,
    declineCode: string | null = null
): void {
    if (!message && !code && !declineCode) {
        return;
    }
    lastTerminalFailure = { message: message ?? '', code: code ?? null, declineCode };

    const waiters = terminalFailureWaiters;
    terminalFailureWaiters = [];
    for (const resolve of waiters) {
        resolve();
    }
}

/**
 * Resolve as soon as a native Failed event arrives (or after timeoutMs). Used to
 * abort the discovery wait the moment the SDK reports a hard failure.
 */
function waitForTerminalFailure(timeoutMs: number): Promise<void> {
    if (lastTerminalFailure) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        const resolver = () => {
            window.clearTimeout(timeoutId);
            resolve();
        };

        const timeoutId = window.setTimeout(() => {
            terminalFailureWaiters = terminalFailureWaiters.filter((waiter) => waiter !== resolver);
            resolve();
        }, timeoutMs);

        terminalFailureWaiters.push(resolver);
    });
}

/**
 * Return just the first sentence of a raw SDK message, so user-facing fallbacks
 * keep the actionable part ("Developer Options must not be enabled…") and drop
 * developer-only tails ("…set TapToPayDiscoveryConfiguration.isSimulated to true").
 */
function firstSentence(message: string): string {
    const trimmed = message.trim();
    const periodIdx = trimmed.indexOf('. ');
    return periodIdx > 0 ? trimmed.slice(0, periodIdx + 1) : trimmed;
}

function resetAttemptRuntimeState(): void {
    lastTokenError = null;
    tokenRequestAttemptId = null;
    tokenErrorAttemptId = null;
    resetDiscoveredReadersEventState();
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

/**
 * Race a best-effort native call against a timeout. Native housekeeping calls
 * must never block the JS state machine — see NATIVE_HOUSEKEEPING_TIMEOUT_MS.
 */
function withTimeout<T>(operation: Promise<T>, ms: number): Promise<T> {
    let timeoutId = 0;
    const timeoutPromise = new Promise<T>((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error('native call timed out')), ms);
    });
    return Promise.race([operation, timeoutPromise]).finally(() => window.clearTimeout(timeoutId));
}

/**
 * Query the native SDK for the actual connected reader and reconcile the local
 * `connectionStatus` mirror with it. The JS mirror can desync from native truth
 * after a webview reload (Capgo OTA, crash recovery) or a watchdog race, leaving
 * connect failing with "already connected" while cleanupTerminal() skips the
 * disconnect. getConnectedReader() is a cheap native call, so we trust it over
 * the mirror at the decision points that previously deadlocked recovery.
 *
 * On error/timeout (e.g. plugin not present on web) we fall back to the mirror.
 */
async function hasNativeConnectedReader(): Promise<boolean> {
    // getConnectedReader() resolves to Terminal.getInstance() natively, which
    // THROWS if the SDK has never been initialized. If we have not initialized in
    // this session and the mirror shows nothing connected, there is definitively
    // no reader — skip the native call so we don't blow up on a fresh app start
    // (e.g. the hook's unmount cleanup before any payment was attempted).
    if (!initialized && connectionStatus === 'not_connected') {
        return false;
    }

    try {
        const result = await withTimeout(StripeTerminal.getConnectedReader(), NATIVE_HOUSEKEEPING_TIMEOUT_MS);
        const connected = Boolean((result as any)?.reader);
        connectionStatus = connected ? 'connected' : 'not_connected';
        return connected;
    } catch {
        return connectionStatus === 'connected';
    }
}

function isAlreadyConnectedError(message: string | null): boolean {
    return (message ?? '').toLowerCase().includes('already connected');
}

function isPendingFetchConnectionTokenError(message: string | null): boolean {
    return (message ?? '').includes('pending fetchConnectionToken');
}

function isContactlessTransactionCanceledError(message: string | null): boolean {
    return (message ?? '').toLowerCase().includes('contactless transaction was canceled');
}

/**
 * "The command was canceled" is what the SDK reports when the reader UI is
 * dismissed — on iOS that is the user closing Apple's own Tap to Pay sheet.
 * It is a deliberate stop, not a fault.
 */
function isReaderDismissedError(message: string | null): boolean {
    return (message ?? '').toLowerCase().includes('command was cancel');
}

/**
 * A short outcome headline for a failed Tap to Pay attempt (App Review §5.8: the
 * user must be clearly informed whether the outcome was approved, declined, or
 * timed out). The detailed reason still shows underneath; this just makes the
 * outcome unmistakable at a glance instead of a generic "Payment Failed".
 */
/**
 * Stripe's decline_code vocabulary (underscored or spaced) plus plain wording.
 * Only a fallback — `declineCode` on the native Failed event is authoritative,
 * but Android has been seen emitting an empty Failed payload, leaving only the
 * rejection message ("Your card was declined.") to go on.
 */
const CARD_DECLINE_PATTERN =
    /declin|do[ _]not[ _]honor|insufficient|invalid[ _]account|lost[ _]card|stolen[ _]card|expired[ _]card|pickup[ _]card|restricted[ _]card|card[ _]not[ _]supported|not[ _]permitted|call[ _]issuer|transaction[ _]not[ _]allowed|card[ _]velocity|fraudulent|security[ _]violation|card was not authorized/;

/**
 * Whether a failure was the *issuer* declining the card, rather than anything
 * wrong with the reader, the device or the app.
 *
 * This distinction matters beyond wording: a decline means the reader just read a
 * card and completed a round trip to Stripe, so it is provably healthy and must
 * NOT be torn down and reconnected before the next attempt.
 *
 * `declineCode` is set by the SDK only on a real decline, so it is checked first.
 * It is cleared per attempt by `startTapToPayAttempt`, so it cannot leak from an
 * earlier payment.
 */
export function isCardDeclineError(message: string | null | undefined): boolean {
    if (lastTerminalFailure?.declineCode) {
        return true;
    }
    return CARD_DECLINE_PATTERN.test((message ?? '').toLowerCase());
}

export function tapToPayFailureHeadline(message: string | null): string {
    if (isCardDeclineError(message)) {
        return 'Card Declined';
    }

    // Deliberately no bare "expired" here — that belongs to expired_card above.
    if (/timed out|timeout|took too long|no response|did not respond/.test((message ?? '').toLowerCase())) {
        return 'Timed Out';
    }

    return 'Payment Failed';
}

function normalizeCollectPaymentErrorMessage(message: string | null): string {
    if (isContactlessTransactionCanceledError(message)) {
        return `Tap was canceled before the card was accepted. Hold one contactless card steady ${cardPositionHint()} until it responds, then try again.`;
    }

    if (isReaderDismissedError(message)) {
        return 'Tap to Pay was canceled.';
    }

    return message ?? 'Collect payment method failed';
}

/**
 * Translate a raw Stripe Terminal discovery failure (now propagated from the
 * patched native plugin instead of being swallowed) into an actionable,
 * user-facing message. Returns null for errors we don't have a specific hint
 * for, so the caller can fall back to the raw text.
 */
function mapDiscoveryError(raw: string): string | null {
    const lower = raw.toLowerCase();

    if (isAndroid()) {
        if (lower.includes('developer options')
            || lower.includes('insecure_environment')
            || lower.includes('insecure environment')) {
            return 'Tap to Pay requires Developer Options to be OFF. Go to Settings → Developer Options and disable it (and turn off USB debugging), then try again.';
        }
        if (lower.includes('nfc_disabled') || (lower.includes('nfc') && lower.includes('disabled'))) {
            return 'Turn on NFC for Tap to Pay. Open Settings → Connected devices → NFC, enable it, then try again.';
        }
        if (lower.includes('location_services_disabled')
            || (lower.includes('location services') && lower.includes('disabled'))) {
            return 'Turn on Location for Tap to Pay. Open Settings → Location, enable it, then try again.';
        }
        if (lower.includes('outdated') && lower.includes('security')) {
            return "This phone's security patch is more than 12 months old, which Tap to Pay does not allow. Install the latest system update, then try again.";
        }
        if (lower.includes('unsupported_device')
            || lower.includes('unsupported_mobile_device')
            || lower.includes('not supported')
            || lower.includes('unsupported')) {
            return 'This phone does not meet Stripe Tap to Pay requirements (a hardware-backed keystore, a recent security patch). Use a supported Android device.';
        }
        return null;
    }

    if (isIOS()) {
        if (lower.includes('nfc_disabled') || (lower.includes('nfc') && lower.includes('disabled'))) {
            return 'NFC is required for Tap to Pay. Open Settings and turn on NFC, then try again.';
        }
        if (lower.includes('location_services_disabled')
            || (lower.includes('location services') && lower.includes('disabled'))) {
            return 'Turn on Location for Tap to Pay. Open Settings → Privacy & Security → Location Services, enable it, then try again.';
        }
        if (lower.includes('unsupported_device')
            || lower.includes('unsupported_mobile_device')
            || lower.includes('not supported')
            || lower.includes('unsupported')) {
            return 'This iPhone does not meet Stripe Tap to Pay requirements. Update to the latest iOS and use a supported iPhone.';
        }
    }

    return null;
}

function updateDiscoveredReadersFromEvent(readers: any[] | undefined): void {
    const normalizedReaders = Array.isArray(readers) ? readers : [];

    latestDiscoveredReadersFromEvent = normalizedReaders;
    latestDiscoveredReadersAt = normalizedReaders.length > 0 ? Date.now() : 0;

    if (normalizedReaders.length === 0 || discoveredReaderWaiters.length === 0) {
        return;
    }

    const waiters = discoveredReaderWaiters;
    discoveredReaderWaiters = [];
    for (const resolve of waiters) {
        resolve(normalizedReaders);
    }
}

function getLatestDiscoveredReaderEventAgeMs(): number | null {
    if (latestDiscoveredReadersAt <= 0) {
        return null;
    }

    return Date.now() - latestDiscoveredReadersAt;
}

function waitForDiscoveredReadersFromEvent(timeoutMs: number): Promise<any[]> {
    const eventAgeMs = latestDiscoveredReadersAt > 0 ? Date.now() - latestDiscoveredReadersAt : Number.POSITIVE_INFINITY;
    if (latestDiscoveredReadersFromEvent.length > 0 && eventAgeMs <= timeoutMs) {
        return Promise.resolve(latestDiscoveredReadersFromEvent);
    }

    return new Promise((resolve) => {
        const resolver = (readers: any[]) => {
            window.clearTimeout(timeoutId);
            resolve(readers);
        };

        const timeoutId = window.setTimeout(() => {
            discoveredReaderWaiters = discoveredReaderWaiters.filter((waiter) => waiter !== resolver);
            resolve([]);
        }, timeoutMs);

        discoveredReaderWaiters.push(resolver);
    });
}

async function loadTapToPayDiagnostics(): Promise<TapToPayDiagnosticEntry[]> {
    if (diagnosticsCache) {
        return diagnosticsCache;
    }

    if (!diagnosticsLoadPromise) {
        diagnosticsLoadPromise = (async () => {
            const raw = await getStorageItem(TAP_TO_PAY_DIAGNOSTICS_STORAGE_KEY);
            if (!raw) {
                diagnosticsCache = [];
                return diagnosticsCache;
            }

            try {
                const parsed = JSON.parse(raw);
                diagnosticsCache = Array.isArray(parsed) ? parsed : [];
            } catch {
                diagnosticsCache = [];
            }

            return diagnosticsCache;
        })();
    }

    return diagnosticsLoadPromise;
}

/**
 * Persist the buffered entries. Flushes are chained so two overlapping writes
 * can't clobber each other, and every failure is swallowed — diagnostics must
 * never break the payment flow.
 */
function flushTapToPayDiagnostics(): Promise<void> {
    window.clearTimeout(diagnosticsFlushTimerId);
    diagnosticsFlushTimerId = 0;

    diagnosticsFlushPromise = (diagnosticsFlushPromise ?? Promise.resolve()).then(async () => {
        const batch = pendingDiagnostics;
        if (batch.length === 0) {
            return;
        }
        pendingDiagnostics = [];

        try {
            const entries = [...await loadTapToPayDiagnostics(), ...batch]
                .slice(-TAP_TO_PAY_DIAGNOSTICS_MAX_ENTRIES);
            diagnosticsCache = entries;
            diagnosticsLoadPromise = Promise.resolve(entries);
            await setStorageItem(TAP_TO_PAY_DIAGNOSTICS_STORAGE_KEY, JSON.stringify(entries));
        } catch {
            // Storage failed — the entries are already gone from the buffer, but
            // losing breadcrumbs is strictly better than breaking a payment.
        }
    });

    return diagnosticsFlushPromise;
}

/**
 * Append a diagnostic breadcrumb. Synchronous by design: this sits on the
 * payment critical path and is called for every native SDK event, so it only
 * touches memory. Storage is written by the debounced flush above.
 */
function recordTapToPayDiagnostic(
    stage: string,
    message: string,
    details?: Record<string, unknown>,
    level: TapToPayDiagnosticLevel = 'info'
): void {
    try {
        const attemptId = currentAttemptId ?? createAttemptId();
        currentAttemptId = attemptId;

        pendingDiagnostics.push({
            attemptId,
            timestamp: new Date().toISOString(),
            level,
            stage,
            message,
            ...(details ? { details: sanitizeDiagnosticsValue(details) as Record<string, unknown> } : {}),
        });

        if (pendingDiagnostics.length > TAP_TO_PAY_DIAGNOSTICS_MAX_ENTRIES) {
            pendingDiagnostics = pendingDiagnostics.slice(-TAP_TO_PAY_DIAGNOSTICS_MAX_ENTRIES);
        }
    } catch {
        return;
    }

    // Flush errors immediately: a crash right after one is exactly the case
    // where the entry matters most, and the debounce window would lose it.
    if (level === 'error') {
        void flushTapToPayDiagnostics();
        return;
    }

    if (!diagnosticsFlushTimerId) {
        diagnosticsFlushTimerId = window.setTimeout(() => {
            void flushTapToPayDiagnostics();
        }, DIAGNOSTICS_FLUSH_DEBOUNCE_MS);
    }
}

// ── Time-to-ready telemetry ───────────────────────────────────────────────────

/**
 * Mark the moment the user asked to pay. Paired with the PaymentStatusChange
 * `WAITING_FOR_INPUT` event — the point at which a card can actually be
 * presented — to measure what Apple's 1s/90% requirement is defined against.
 */
export function markTapToPayIntent(): void {
    readyMarkAt = performance.now();
}

/**
 * Called from whichever native event first says a card may be presented.
 * Consumes the pending intent mark and records the duration; returns the rounded
 * value for the diagnostic breadcrumb, or null if there was nothing to measure
 * (no mark, or another event already claimed it).
 */
function noteReaderReadyForTap(): number | null {
    if (readyMarkAt == null) {
        return null;
    }

    const elapsed = performance.now() - readyMarkAt;
    readyMarkAt = null;
    void recordTapToPayReadyDuration(elapsed);
    return Math.round(elapsed);
}

async function loadTapToPayTimings(): Promise<number[]> {
    if (timingsCache) {
        return timingsCache;
    }

    try {
        const raw = await getStorageItem(TAP_TO_PAY_TIMINGS_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        timingsCache = Array.isArray(parsed)
            ? parsed.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))
            : [];
    } catch {
        timingsCache = [];
    }

    return timingsCache;
}

/** Nearest-rank percentile. `sorted` must be ascending and non-empty. */
function percentile(sorted: number[], p: number): number {
    const rank = Math.ceil((p / 100) * sorted.length);
    return sorted[Math.min(sorted.length - 1, Math.max(0, rank - 1))];
}

function summarizeTimings(timings: number[]): Record<string, unknown> {
    if (timings.length === 0) {
        return { samples: 0 };
    }

    const sorted = [...timings].sort((a, b) => a - b);
    const withinBudget = timings.filter((value) => value <= TAP_TO_PAY_READY_BUDGET_MS).length;

    return {
        samples: sorted.length,
        p50Ms: percentile(sorted, 50),
        p90Ms: percentile(sorted, 90),
        maxMs: sorted[sorted.length - 1],
        withinBudgetPct: Math.round((withinBudget / timings.length) * 100),
    };
}

async function recordTapToPayReadyDuration(readyMs: number): Promise<void> {
    try {
        const timings = [...await loadTapToPayTimings(), Math.round(readyMs)]
            .slice(-TAP_TO_PAY_TIMINGS_MAX_ENTRIES);
        timingsCache = timings;

        recordTapToPayDiagnostic('timing', 'Reader ready for tap', {
            readyMs: Math.round(readyMs),
            budgetMs: TAP_TO_PAY_READY_BUDGET_MS,
            ...summarizeTimings(timings),
        });

        await setStorageItem(TAP_TO_PAY_TIMINGS_STORAGE_KEY, JSON.stringify(timings));
    } catch {
        // Telemetry must never break a payment.
    }
}

function locationHint(locationId: string | undefined): string | null {
    if (!locationId) {
        return null;
    }

    return locationId.length <= 10 ? locationId : `${locationId.slice(0, 6)}...${locationId.slice(-4)}`;
}

function publishTapToPayProgress(update: TapToPayProgressUpdate): void {
    currentProgressListener?.(update);
}

function updateTapToPayProgress(
    stage: TapToPayProgressStage,
    message: string,
    source: 'local' | 'terminal' = 'local'
): void {
    publishTapToPayProgress({ stage, message, source });
}

/**
 * Register the UI that should receive progress updates, or `null` to detach.
 *
 * This is a plain setter rather than a wrapper around each call because the warm
 * path is no longer owned by a component: a background warm runs with no
 * listener attached, and a modal that opens midway through it must be able to
 * pick up the *remaining* progress events. A save/restore wrapper would have the
 * background caller clear the listener the modal just installed.
 */
export function setTapToPayProgressListener(
    listener: ((update: TapToPayProgressUpdate) => void) | null
): ((update: TapToPayProgressUpdate) => void) | null {
    const previous = currentProgressListener;
    currentProgressListener = listener;
    return previous;
}

function getReaderDisplayMessage(messageType: unknown, message: unknown): string | null {
    if (typeof message === 'string' && message.trim().length > 0) {
        return message.trim();
    }

    switch (String(messageType ?? '').toUpperCase()) {
        case 'CHECK_MOBILE_DEVICE':
            return 'Check the phone for Tap to Pay instructions.';
        case 'RETRY_CARD':
            return 'Card was not read. Hold it steady and try again.';
        case 'REMOVE_CARD':
            return 'Remove the card from the phone.';
        case 'MULTIPLE_CONTACTLESS_CARDS_DETECTED':
            return 'Multiple contactless cards were detected. Present only one card.';
        case 'TRY_ANOTHER_READ_METHOD':
            return 'Try a different read method.';
        case 'TRY_ANOTHER_CARD':
            return 'Try another card.';
        case 'CARD_REMOVED_TOO_EARLY':
            return 'Hold the card in place until the phone responds.';
        default:
            return null;
    }
}

function getTapToPayProgressFromEvent(eventName: TerminalEventsEnum, payload: any): TapToPayProgressUpdate | null {
    if (eventName === TerminalEventsEnum.ConnectionStatusChange) {
        switch (String(payload?.status ?? '').toUpperCase()) {
            case 'DISCOVERING':
                return {
                    stage: 'discovering_reader',
                    message: 'Finding Tap to Pay reader on this phone…',
                    source: 'terminal',
                };
            case 'CONNECTING':
            case 'RECONNECTING':
                return {
                    stage: 'connecting_reader',
                    message: 'Connecting Tap to Pay on this phone…',
                    source: 'terminal',
                };
            case 'CONNECTED':
                return {
                    stage: 'connecting_reader',
                    message: 'Reader connected. Preparing for tap…',
                    source: 'terminal',
                };
            default:
                return null;
        }
    }

    if (eventName === TerminalEventsEnum.PaymentStatusChange) {
        switch (String(payload?.status ?? '').toUpperCase()) {
            case 'READY':
                return {
                    stage: 'connecting_reader',
                    message: 'Reader connected. Starting Tap to Pay…',
                    source: 'terminal',
                };
            case 'WAITING_FOR_INPUT':
                return {
                    stage: 'waiting_for_tap',
                    message: `Ready for tap. Hold one contactless card ${cardPositionHint()}.`,
                    source: 'terminal',
                };
            case 'PROCESSING':
                return {
                    stage: 'processing_payment',
                    message: 'Card detected. Processing payment…',
                    source: 'terminal',
                };
            case 'NOT_READY':
                return {
                    stage: 'connecting_reader',
                    message: 'Preparing the Tap to Pay reader…',
                    source: 'terminal',
                };
            default:
                return null;
        }
    }

    if (eventName === TerminalEventsEnum.RequestReaderInput) {
        const options = Array.isArray(payload?.options)
            ? payload.options.map((option: unknown) => String(option).toUpperCase())
            : [];
        const message = typeof payload?.message === 'string' && payload.message.trim().length > 0
            ? payload.message.trim()
            : null;

        return {
            stage: 'waiting_for_tap',
            message: message ?? (options.includes('TAP')
                ? `Ready for tap. Hold one contactless card ${cardPositionHint()}.`
                : 'Reader is waiting for customer input.'),
            source: 'terminal',
        };
    }

    if (eventName === TerminalEventsEnum.RequestDisplayMessage) {
        const message = getReaderDisplayMessage(payload?.messageType, payload?.message);
        if (!message) {
            return null;
        }

        return {
            stage: 'waiting_for_tap',
            message,
            source: 'terminal',
        };
    }

    // §3.6: first-run Tap to Pay provisioning. The iOS TapToPayReader delegate emits
    // these, so the ~45s install can show a determinate bar instead of a frozen
    // "Connecting…". `installAvailableUpdate` is not called explicitly — the SDK runs
    // a mandatory update inline during connect, which is exactly this sequence.
    if (eventName === TerminalEventsEnum.StartInstallingUpdate) {
        return {
            stage: 'configuring_reader',
            message: 'Setting up Tap to Pay on this phone…',
            source: 'terminal',
            progress: 0,
        };
    }

    if (eventName === TerminalEventsEnum.ReaderSoftwareUpdateProgress) {
        const raw = typeof payload?.progress === 'number' ? payload.progress : 0;
        const progress = Math.min(1, Math.max(0, raw));
        return {
            stage: 'configuring_reader',
            message: `Setting up Tap to Pay on this phone… ${Math.round(progress * 100)}%`,
            source: 'terminal',
            progress,
        };
    }

    if (eventName === TerminalEventsEnum.FinishInstallingUpdate) {
        return {
            stage: 'configuring_reader',
            message: 'Almost ready…',
            source: 'terminal',
            progress: 1,
        };
    }

    return null;
}

async function registerDiagnosticListeners(): Promise<void> {
    await ensureAppStateListenerRegistered();

    if (diagnosticListenerHandles.length > 0) {
        return;
    }

    const addDiagnosticListener = (
        eventName: TerminalEventsEnum,
        listener: (payload: any) => void
    ): Promise<PluginListenerHandle> => {
        return (StripeTerminal as any).addListener(eventName, listener);
    };

    const listenerConfigs: Array<[TerminalEventsEnum, (payload: any) => Record<string, unknown> | undefined]> = [
        [TerminalEventsEnum.ConnectionStatusChange, ({ status }) => {
            lastTerminalProgressAt = Date.now();
            const upper = String(status ?? '').toUpperCase();
            if (upper === 'CONNECTING' || upper === 'RECONNECTING') {
                connectionStatus = 'connecting';
            } else if (upper === 'CONNECTED') {
                connectionStatus = 'connected';
            } else if (upper === 'NOT_CONNECTED' || upper === 'DISCONNECTED') {
                connectionStatus = 'not_connected';
            }
            return { status, connectionStatus };
        }],
        [TerminalEventsEnum.PaymentStatusChange, ({ status }) => {
            lastTerminalProgressAt = Date.now();

            // WAITING_FOR_INPUT is the real "a card may now be presented" moment.
            if (String(status ?? '').toUpperCase() !== 'WAITING_FOR_INPUT') {
                return { status };
            }

            const readyMs = noteReaderReadyForTap();
            return readyMs == null ? { status } : { status, readyMs };
        }],
        [TerminalEventsEnum.DiscoveredReaders, ({ readers }) => ({
            readerCount: Array.isArray(readers) ? readers.length : 0,
            readers: Array.isArray(readers) ? readers.slice(0, 2).map((reader) => summarizeReader(reader)) : [],
        })],
        [TerminalEventsEnum.ConnectedReader, () => {
            connectionStatus = 'connected';
            return { connectionStatus };
        }],
        [TerminalEventsEnum.DisconnectedReader, ({ reason }) => {
            connectionStatus = 'not_connected';
            return { reason: reason ?? null, connectionStatus };
        }],
        [TerminalEventsEnum.UnexpectedReaderDisconnect, ({ reader }) => {
            connectionStatus = 'not_connected';
            return { reader: summarizeReader(reader), connectionStatus };
        }],
        [TerminalEventsEnum.RequestDisplayMessage, ({ messageType, message }) => {
            // Reader UI activity (incl. first-run provisioning) means the native
            // connect is alive — count it as progress so the watchdog disarms.
            lastTerminalProgressAt = Date.now();
            return { messageType, message };
        }],
        [TerminalEventsEnum.RequestReaderInput, ({ options, message }) => {
            lastTerminalProgressAt = Date.now();

            // iOS never emits PaymentStatusChange — this is the only "a card may
            // now be presented" signal there, so the budget is measured off it too.
            const wantsTap = Array.isArray(options)
                && options.some((option: unknown) => String(option).toUpperCase() === 'TAP');
            const readyMs = wantsTap ? noteReaderReadyForTap() : null;

            return readyMs == null ? { message, options } : { message, options, readyMs };
        }],
        [TerminalEventsEnum.StartInstallingUpdate, () => {
            // First-run provisioning is alive — keep the connect watchdog disarmed.
            lastTerminalProgressAt = Date.now();
            return { phase: 'start' };
        }],
        [TerminalEventsEnum.ReaderSoftwareUpdateProgress, ({ progress }) => {
            lastTerminalProgressAt = Date.now();
            return { progress };
        }],
        [TerminalEventsEnum.FinishInstallingUpdate, (payload) => {
            lastTerminalProgressAt = Date.now();
            return { error: payload?.error ?? null };
        }],
        [TerminalEventsEnum.CollectedPaymentIntent, () => undefined],
        [TerminalEventsEnum.ConfirmedPaymentIntent, () => undefined],
        [TerminalEventsEnum.Canceled, () => undefined],
        [TerminalEventsEnum.Failed, (payload) => {
            const message = typeof payload?.message === 'string' ? payload.message : null;
            const code = typeof payload?.code === 'string' ? payload.code : null;
            // The SDK sets declineCode only when the issuer actually declined, so it
            // is the definitive signal for the §5.8 outcome headline — far better
            // than pattern-matching the human message, which varies per decline
            // reason ("invalid account", "do not honor", …).
            const declineCode = typeof payload?.declineCode === 'string' ? payload.declineCode : null;
            recordTerminalFailure(message, code, declineCode);
            return { payload: sanitizeDiagnosticsValue(payload) };
        }],
    ];

    diagnosticListenerHandles = await Promise.all(
        listenerConfigs.map(([eventName, getDetails]) =>
            addDiagnosticListener(eventName, (payload: any) => {
                if (eventName === TerminalEventsEnum.DiscoveredReaders) {
                    updateDiscoveredReadersFromEvent(payload?.readers ?? []);
                }
                const progressUpdate = getTapToPayProgressFromEvent(eventName, payload);
                if (progressUpdate) {
                    publishTapToPayProgress(progressUpdate);
                }
                recordTapToPayDiagnostic(`event:${eventName}`, 'Terminal event received', getDetails(payload));
            })
        )
    );
}

async function ensureTokenListenerRegistered(): Promise<void> {
    // Keep the existing listener if one is already registered.
    // The listener reads currentAttemptId at call time, so it automatically
    // uses the correct attempt context for new payments. Removing and re-adding
    // the listener breaks the native event bridge on some Capacitor versions,
    // causing "Timed out waiting for connection token" on subsequent payments.
    if (tokenListenerHandle) {
        return;
    }

    const setConnectionTokenWithRetry = async (token: string): Promise<void> => {
        let lastError: unknown = null;

        for (let attempt = 1; attempt <= SET_CONNECTION_TOKEN_RETRY_ATTEMPTS; attempt += 1) {
            try {
                await StripeTerminal.setConnectionToken({ token });

                if (attempt > 1) {
                    recordTapToPayDiagnostic('token', 'setConnectionToken succeeded after retry', {
                        attempt,
                    }, 'warn');
                }

                return;
            } catch (err: unknown) {
                const message = extractMessage(err);
                lastError = err;

                if (!isPendingFetchConnectionTokenError(message) || attempt === SET_CONNECTION_TOKEN_RETRY_ATTEMPTS) {
                    throw err;
                }

                recordTapToPayDiagnostic('token', 'Retrying setConnectionToken after native bridge was not ready', {
                    attempt,
                    delayMs: SET_CONNECTION_TOKEN_RETRY_DELAY_MS,
                    error: message,
                }, 'warn');

                await delay(SET_CONNECTION_TOKEN_RETRY_DELAY_MS);
            }
        }

        throw lastError;
    };

    tokenListenerHandle = await StripeTerminal.addListener(
        TerminalEventsEnum.RequestedConnectionToken,
        async () => {
            const attemptId = currentAttemptId;
            tokenRequestAttemptId = attemptId;
            lastTokenRequestAt = Date.now();
            recordTapToPayDiagnostic('token', 'Stripe Terminal requested a connection token', {
                attemptId,
            });

            try {
                const { secret } = await apiService.createTerminalConnectionToken();
                if (!secret) throw new Error('Connection token API returned an empty secret');
                lastTokenError = null;
                tokenErrorAttemptId = null;
                recordTapToPayDiagnostic('token', 'Connection token received from backend', {
                    attemptId,
                    receivedSecret: true,
                    secretLength: secret.length,
                });

                await setConnectionTokenWithRetry(secret);

                recordTapToPayDiagnostic('token', 'Connection token passed to Stripe Terminal', {
                    attemptId,
                });
            } catch (err: any) {
                lastTokenError = extractMessage(err);
                tokenErrorAttemptId = attemptId;
                recordTapToPayDiagnostic('token', 'Connection token request failed', {
                    attemptId,
                    error: lastTokenError,
                }, 'error');

                if (!isPendingFetchConnectionTokenError(lastTokenError)) {
                    try {
                        await StripeTerminal.setConnectionToken({ token: '' });
                    } catch {
                        // Ignore follow-up bridge failures while surfacing the original token error.
                    }
                }
            }
        }
    );
}

export function startTapToPayAttempt(context: Record<string, unknown> = {}): string {
    resetAttemptRuntimeState();
    currentAttemptId = createAttemptId();
    recordTapToPayDiagnostic('attempt', 'Tap to Pay attempt started', context);
    return currentAttemptId;
}

export async function getTapToPayDiagnosticsReport(): Promise<string> {
    // Include anything still buffered so the report is never a flush behind.
    const entries = [...await loadTapToPayDiagnostics(), ...pendingDiagnostics]
        .slice(-TAP_TO_PAY_DIAGNOSTICS_MAX_ENTRIES);
    const lastEntry = entries.length > 0 ? entries[entries.length - 1] : null;
    const latestAttemptId = currentAttemptId ?? lastEntry?.attemptId ?? null;

    // Filtering strictly by attemptId used to hide the most important part of the
    // report. The reader is warmed in the background *before* startTapToPayAttempt
    // rotates the id, so init/discovery/connect breadcrumbs — exactly the phase
    // that fails — carry the previous attempt's id. Include everything from the
    // lead-up window too.
    const attemptEntries = latestAttemptId
        ? entries.filter((entry) => entry.attemptId === latestAttemptId)
        : [];
    const attemptStartedAt = attemptEntries.length > 0
        ? Date.parse(attemptEntries[0].timestamp)
        : Number.NaN;
    const windowStartsAt = Number.isNaN(attemptStartedAt)
        ? Number.NaN
        : attemptStartedAt - DIAGNOSTICS_LEAD_UP_WINDOW_MS;

    const relevantEntries = Number.isNaN(windowStartsAt)
        ? entries.slice(-40)
        : entries.filter((entry) => {
            if (entry.attemptId === latestAttemptId) return true;
            const at = Date.parse(entry.timestamp);
            return !Number.isNaN(at) && at >= windowStartsAt && at <= attemptStartedAt;
        });

    const timings = await loadTapToPayTimings();
    const summary = summarizeTimings(timings);

    const lines = [
        'Tap to Pay diagnostics',
        `Generated: ${new Date().toISOString()}`,
        `Attempt: ${latestAttemptId ?? 'unknown'}`,
        '',
        `Time to ready (budget ${TAP_TO_PAY_READY_BUDGET_MS}ms): ${
            timings.length === 0
                ? 'no samples yet'
                : `p50 ${summary.p50Ms}ms, p90 ${summary.p90Ms}ms, max ${summary.maxMs}ms, `
                    + `${summary.withinBudgetPct}% within budget over ${summary.samples} samples`
        }`,
        '',
    ];

    for (const entry of relevantEntries) {
        lines.push(`${entry.timestamp} [${entry.level}] ${entry.stage}: ${entry.message}${stringifyDiagnosticsDetails(entry.details)}`);
    }

    if (relevantEntries.length === 0) {
        lines.push('No Tap to Pay diagnostics have been captured yet.');
    }

    return lines.join('\n');
}

// ── Support check ─────────────────────────────────────────────────────────────

/**
 * Live, native device-capability check (Android only) using the patched plugin's
 * supportsTapToPay() method. Catches phones that pass the coarse "Android 13+"
 * gate but can never run Tap to Pay: NFC missing/disabled, no hardware-backed
 * keystore, stale security patch, or not Google Play certified.
 *
 * Returns an unsupported result when a hard blocker is found, otherwise null
 * (let the caller proceed). The deep SDK portion (sdkChecked) requires the
 * Terminal to be initialized, so the mount-time call typically only evaluates
 * the live NFC portion; the in-flow call (after initializeTerminal) gets both.
 */
async function checkNativeTapToPayCapability(): Promise<TapToPaySupportResult | null> {
    // supportsTapToPay() only exists in the Android plugin patch. Calling it on
    // iOS is a guaranteed bridge round trip that rejects with "not implemented",
    // and this runs on every support check — several times per payment screen.
    if (!isAndroid()) {
        return null;
    }

    try {
        const result: any = await (StripeTerminal as any).supportsTapToPay({
            isTest: ENV.STRIPE_PUBLISHABLE_KEY.startsWith('pk_test_'),
        });

        if (result?.nfcPresent === false) {
            return {
                supported: false,
                reason: 'nfc_unavailable',
                message: 'This phone has no NFC hardware, which Tap to Pay requires.',
            };
        }
        if (result?.nfcEnabled === false) {
            return {
                supported: false,
                reason: 'nfc_unavailable',
                message: 'NFC is turned off. Open Settings → Connected devices → NFC, enable it, then try again.',
            };
        }

        if (result?.sdkChecked === true && result?.sdkSupported === false) {
            const detail = typeof result?.sdkError === 'string' && result.sdkError.trim().length > 0
                ? ` (${result.sdkError.trim()})`
                : '';
            return {
                supported: false,
                reason: 'device_unsupported',
                message: `This phone does not meet Stripe Tap to Pay requirements${detail}. It needs a hardware-backed keystore, a recent security patch, and Google Play certification.`,
            };
        }

        return null;
    } catch (err) {
        // Method missing (older native build / non-Android) or a transient failure:
        // don't block the user — fall back to the coarser checks.
        recordTapToPayDiagnostic('support', 'Native Tap to Pay capability check unavailable', {
            error: extractMessage(err),
        }, 'warn');
        return null;
    }
}

/**
 * Multi-layer eligibility check for Tap to Pay.
 *
 * Immutable checks are cached after the first call (OS version never changes).
 * Live checks (NFC, device capability, network) are evaluated fresh on every call.
 */
export async function checkTapToPaySupport(): Promise<TapToPaySupportResult> {
    // 1. Must be native Capacitor (not web browser)
    if (!isNative()) {
        return {
            supported: false,
            reason: 'not_native',
            message: 'Tap to Pay only works inside the installed Android or iOS app, not in a browser tab.'
        };
    }

    // 2. Must be a supported native platform (Android or iOS)
    if (!isAndroid() && !isIOS()) {
        return {
            supported: false,
            reason: 'unsupported_platform',
            message: 'Tap to Pay is only available in the Android and iOS apps.'
        };
    }

    // 3. Device environment checks (cached — device model/OS do not change at runtime)
    if (!cachedSupportResult) {
        const info = await getDeviceInfoSafe();
        const deviceLabel = info ? formatDeviceLabel(info) : 'This device';

        if (isAndroid()) {
            const osVersionNum = info ? parseAndroidVersion(info) : null;
            if (info?.isVirtual) {
                cachedSupportResult = {
                    supported: false,
                    reason: 'virtual_device',
                    message: 'Tap to Pay does not work on Android emulators. Use a physical Android device.'
                };
            } else if (osVersionNum == null || osVersionNum < 13) {
                cachedSupportResult = {
                    supported: false,
                    reason: 'android_version',
                    message: `${deviceLabel} is running Android ${info?.osVersion ?? 'unknown'}. Stripe Tap to Pay now requires Android 13 or later.`
                };
            } else {
                cachedSupportResult = { supported: true };
            }
        } else {
            // iOS: we require iOS 18+ (above Stripe's 16.7 floor) so Apple's own
            // ProximityReaderDiscovery education screens are always available — that
            // lets us drop a bespoke fallback education path and rely on Apple's
            // approved, localized screens. iPhone XS and later all run iOS 18, so
            // this excludes only un-updated devices. Simulator is never supported.
            const iosVersion = info ? parseIOSVersion(info) : null;
            if (info?.isVirtual) {
                cachedSupportResult = {
                    supported: false,
                    reason: 'virtual_device',
                    message: 'Tap to Pay does not work on the iOS Simulator. Use a physical iPhone.'
                };
            } else if (!iosVersion || iosVersion.major < 18) {
                cachedSupportResult = {
                    supported: false,
                    reason: 'ios_version',
                    message: `${deviceLabel} is running iOS ${info?.osVersion ?? 'unknown'}. Update to iOS 18 or later to use Tap to Pay on iPhone.`
                };
            } else {
                cachedSupportResult = { supported: true };
            }
        }
    }
    if (!cachedSupportResult.supported) {
        return cachedSupportResult;
    }

    // 4. Live: Network connectivity required for payment confirmation
    if (!navigator.onLine) {
        return {
            supported: false,
            reason: 'offline',
            message: 'Tap to Pay requires an active internet connection.'
        };
    }

    // 5. Live: real Stripe device-capability check (NFC + hardware keystore /
    // security patch / GMS certification). Not cached — NFC and SDK state can
    // change at runtime, and the deep check only becomes available once the
    // Terminal SDK is initialized.
    const capability = await checkNativeTapToPayCapability();
    if (capability) {
        return capability;
    }

    return { supported: true };
}

// ── Initialization ────────────────────────────────────────────────────────────

/**
 * Initialize the Stripe Terminal SDK for this session.
 * Safe to call multiple times — subsequent calls are no-ops.
 *
 * Note: This relies on a patched version of @capacitor-community/stripe-terminal
 * (see patches/ directory) that prevents the plugin from replacing its
 * `tokenProvider` reference on every initialize() call. Without that patch,
 * any JS bundle reload (Capgo OTA, hot reload, app resume) breaks the
 * connection-token bridge with "Stripe Terminal do not pending
 * fetchConnectionToken" errors.
 */
export async function initializeTerminal(): Promise<void> {
    recordTapToPayDiagnostic('initialize', 'Initializing Stripe Terminal', {
        alreadyInitialized: initialized,
        isTestMode: ENV.STRIPE_PUBLISHABLE_KEY.startsWith('pk_test_'),
        hasExistingTokenListener: Boolean(tokenListenerHandle),
    });

    await registerDiagnosticListeners();
    await ensureTokenListenerRegistered();

    if (initialized) {
        recordTapToPayDiagnostic('initialize', 'Reusing existing Stripe Terminal instance', {
            tokenListenerRefreshed: true,
            connectionStatus,
        });
        return;
    }

    // Register the token provider callback BEFORE initialize(), so the SDK
    // can call it immediately when it starts.

    // Derive test mode from the Stripe publishable key prefix, not Vite's build mode.
    // A release APK using test keys (pk_test_...) must pass isTest: true.
    const isTestMode = ENV.STRIPE_PUBLISHABLE_KEY.startsWith('pk_test_');
    try {
        let timeoutId = 0;
        const timeoutPromise = new Promise<never>((_, reject) => {
            timeoutId = window.setTimeout(() => {
                reject(new Error(tapToPayInitializeTimeoutMessage()));
            }, INITIALIZE_TIMEOUT_MS);
        });

        try {
            await Promise.race([
                StripeTerminal.initialize({ isTest: isTestMode }),
                timeoutPromise,
            ]);
        } finally {
            window.clearTimeout(timeoutId);
        }
        initialized = true;
        recordTapToPayDiagnostic('initialize', 'Stripe Terminal initialized');
    } catch (err: any) {
        recordTapToPayDiagnostic('initialize', 'Stripe Terminal initialization failed', {
            error: extractMessage(err),
        }, 'error');
        throw err;
    }
}

// ── Payment collection ────────────────────────────────────────────────────────

/**
 * Full Tap to Pay payment flow:
 *   discoverReaders → connectReader → collectPaymentMethod → confirmPaymentIntent
 *
 * @param clientSecret  The `client_secret` from the `create-intent` endpoint.
 * @param locationId    The Stripe Terminal location ID (tml_xxx) for the company's connected account.
 * @returns             The confirmed PaymentIntent ID (from the SDK event).
 */
async function discoverAndConnectTapToPayReaderImpl(locationId: string | undefined): Promise<void> {
    // Fast path: if we already have a connected reader from a prior attempt
    // on the same payment screen, reuse it. The Stripe SDK is designed to
    // keep one connection across many payments — disconnecting and
    // reconnecting per payment is what causes the multi-second wait.
    //
    // Trust native truth (getConnectedReader) over the JS mirror: after a
    // webview reload the mirror can read 'not_connected' while a reader is
    // still connected natively, which made connect fail with "already
    // connected" forever.
    reusedExistingConnection = false;

    if (await hasNativeConnectedReader()) {
        reusedExistingConnection = true;
        updateTapToPayProgress('connecting_reader', 'Reader already connected. Preparing for tap…');
        recordTapToPayDiagnostic('connect', 'Reusing existing reader connection', {
            connectionStatus,
        });
        return;
    }

    const deviceInfo = await getDeviceInfoSafe();

    recordTapToPayDiagnostic('collect', 'Starting Tap to Pay reader discovery', {
        hasClientSecret: false,
        hasLocationId: Boolean(locationId),
        locationHint: locationHint(locationId),
        isProduction: ENV.IS_PRODUCTION,
        connectionStatus,
        device: summarizeDeviceInfo(deviceInfo),
    });

    const supportResult = await checkTapToPaySupport();
    if (!supportResult.supported) {
        recordTapToPayDiagnostic('support', 'Tap to Pay support check failed', {
            reason: supportResult.reason,
            message: supportResult.message,
            online: navigator.onLine,
        }, 'warn');
        throw new Error(supportResult.message);
    }

    recordTapToPayDiagnostic('support', 'Tap to Pay support check passed', {
        online: navigator.onLine,
    });

    // Determine reader type by *Stripe key*, not by build flavour. A TestFlight or
    // App Review build is not IS_PRODUCTION, and keying off that handed Apple's
    // reviewer a simulated reader — which never shows the system Tap to Pay sheet
    // and fails the review outright. Live keys always mean a real reader.
    // ponytail: the simulator escape hatch stays, it just can't reach a live account.
    const useTapToPay = !ENV.STRIPE_PUBLISHABLE_KEY.startsWith('pk_test_');

    const readerType = useTapToPay
        ? TerminalConnectTypes.TapToPay
        : TerminalConnectTypes.Simulated;

    // The iOS native plugin force-unwraps its stored locationId when connecting a
    // Tap to Pay reader (connectLocalMobileReader → `self.locationId!`), so a nil
    // location crashes the whole app instead of throwing. Fail loudly in JS first.
    if (useTapToPay && !locationId) {
        throw new Error('Tap to Pay could not start: no Stripe Terminal location is set up for this account.');
    }

    updateTapToPayProgress('discovering_reader', 'Finding Tap to Pay reader on this phone…');
    recordTapToPayDiagnostic('discover', 'Discovering readers', {
        readerType,
        useTapToPay,
        timeoutMs: DISCOVERY_OVERALL_TIMEOUT_MS,
    });
    resetDiscoveredReadersEventState();

    // Cancel any discovery still running from a previous (failed/timed-out)
    // attempt. A live discovery makes the next discoverReaders() reject with
    // "already in progress", which would otherwise poison every retry in the
    // session. Bounded: a failed prior discovery can leave a cancelable whose
    // callback never fires, which would otherwise hang every retry here.
    try {
        await withTimeout(StripeTerminal.cancelDiscoverReaders(), NATIVE_HOUSEKEEPING_TIMEOUT_MS);
    } catch {
        // No discovery in progress, or the cancel stalled — safe to ignore.
    }

    // 1. Discover readers.
    //
    // The native plugin resolves the discoverReaders() promise on the FIRST
    // onUpdateDiscoveredReaders callback, which on Android is often empty
    // before NFC warms up. Treat the promise as fire-and-forget and rely on
    // the DiscoveredReaders event for the actual reader list. We accept the
    // first non-empty result from either source, with a single overall
    // timeout — no more "promise resolved with [] then we wait 4s" cliff.
    const discoverPromiseRejection: { error: string | null } = { error: null };
    // Pass locationId on discover, not just connect: the iOS plugin only reads it
    // here (into `self.locationId`) and later force-unwraps it when connecting the
    // Tap to Pay reader. Omitting it on iOS crashes the app at connect time.
    const discoverPromise: Promise<{ readers: any[] }> = StripeTerminal.discoverReaders({
        type: readerType,
        ...(locationId ? { locationId } : {}),
    }).catch((err) => {
        const errorMessage = extractMessage(err);
        discoverPromiseRejection.error = errorMessage;
        recordTapToPayDiagnostic('discover', 'Reader discovery promise rejected', {
            error: errorMessage,
        }, 'warn');
        return { readers: [] as any[] };
    });
    const discoverStartedAt = Date.now();

    const readers = await new Promise<any[]>((resolve) => {
        let resolved = false;
        const finish = (r: any[]) => {
            if (resolved) return;
            resolved = true;
            window.clearTimeout(timeoutId);
            resolve(r);
        };

        const timeoutId = window.setTimeout(() => {
            recordTapToPayDiagnostic('discover', 'Reader discovery timed out', {
                waitedMs: Date.now() - discoverStartedAt,
                timeoutMs: DISCOVERY_OVERALL_TIMEOUT_MS,
            }, 'warn');
            finish([]);
        }, DISCOVERY_OVERALL_TIMEOUT_MS);

        void waitForDiscoveredReadersFromEvent(DISCOVERY_OVERALL_TIMEOUT_MS).then((r) => {
            if (r.length > 0) {
                recordTapToPayDiagnostic('discover', 'Readers received from DiscoveredReaders event', {
                    readerCount: r.length,
                    readers: r.slice(0, 2).map((reader) => summarizeReader(reader)),
                    waitedMs: Date.now() - discoverStartedAt,
                    eventAgeMs: getLatestDiscoveredReaderEventAgeMs(),
                });
                finish(r);
            }
        });

        void discoverPromise.then((result) => {
            const r = result?.readers ?? [];
            if (r.length > 0) {
                recordTapToPayDiagnostic('discover', 'Readers received from discoverReaders promise', {
                    readerCount: r.length,
                    readers: r.slice(0, 2).map((reader) => summarizeReader(reader)),
                    waitedMs: Date.now() - discoverStartedAt,
                });
                finish(r);
            }
        });

        // Abort the wait the moment the SDK reports a hard failure (e.g. insecure
        // environment / unsupported device), instead of running out the full
        // timeout. The zero-readers branch below turns it into a clear message.
        void waitForTerminalFailure(DISCOVERY_OVERALL_TIMEOUT_MS).then(() => {
            if (lastTerminalFailure) {
                recordTapToPayDiagnostic('discover', 'Discovery aborted by Terminal failure event', {
                    code: lastTerminalFailure.code,
                    waitedMs: Date.now() - discoverStartedAt,
                }, 'warn');
                finish([]);
            }
        });
    });

    const discoverError = discoverPromiseRejection.error;
    if (discoverError !== null) {
        // The patched native plugin now propagates real Stripe discovery failures
        // (TAP_TO_PAY_INSECURE_ENVIRONMENT, unsupported device, NFC/location off,
        // stale security patch, "already in progress", …) instead of swallowing
        // them. Surface an actionable message when we recognize the cause.
        const mapped = mapDiscoveryError(discoverError);
        if (mapped) {
            throw new Error(mapped);
        }
        if (readers.length === 0) {
            throw new Error(`Reader discovery failed: ${discoverError}`);
        }
        // Promise rejected but the event still gave us readers — keep going.
    }

    if (readers.length === 0 && lastTerminalFailure) {
        // The SDK emitted a Failed event explaining why discovery produced no
        // readers (the call.reject does not reliably reach the discoverReaders
        // promise once a DISCOVERING status was delivered). Prefer a mapped,
        // actionable message; otherwise show just the first sentence of the raw
        // SDK message so developer-only tails are dropped.
        const failureText = `${lastTerminalFailure.message} ${lastTerminalFailure.code ?? ''}`;
        const mapped = mapDiscoveryError(failureText);
        const friendly = mapped
            ?? `Tap to Pay could not start: ${firstSentence(lastTerminalFailure.message || lastTerminalFailure.code || 'reader discovery failed')}`;

        recordTapToPayDiagnostic('discover', 'Reader discovery failed with a Terminal error event', {
            message: lastTerminalFailure.message,
            code: lastTerminalFailure.code,
            mapped: Boolean(mapped),
        }, 'error');

        throw new Error(friendly);
    }

    if (readers.length === 0) {
        const tokenErrorBelongsToCurrentAttempt = Boolean(lastTokenError)
            && tokenRequestAttemptId === currentAttemptId
            && tokenErrorAttemptId === currentAttemptId;

        if (lastTokenError && !tokenErrorBelongsToCurrentAttempt) {
            recordTapToPayDiagnostic('discover', 'Ignoring stale token error from a different attempt', {
                tokenError: lastTokenError,
                tokenRequestAttemptId,
                tokenErrorAttemptId,
                currentAttemptId,
            }, 'warn');
        }

        // If the token fetch silently failed, that's why readers is empty
        if (tokenErrorBelongsToCurrentAttempt) {
            recordTapToPayDiagnostic('discover', 'Reader discovery returned zero readers after token failure', {
                tokenError: lastTokenError,
            }, 'error');
            throw new Error(`No reader found. Connection token failed: ${lastTokenError}`);
        }

        recordTapToPayDiagnostic('discover', 'Reader discovery returned zero readers', {
            useTapToPay,
            device: summarizeDeviceInfo(deviceInfo),
        }, 'warn');
        throw new Error(
            useTapToPay
                ? getNoReaderMessage(deviceInfo)
                : 'No simulated reader found. Stripe still enforces Tap to Pay device requirements in simulated mode, so use a supported physical Android device with a secure environment.'
        );
    }

    // 2. Connect to the first (only) reader, with a watchdog.
    //
    // If a JS bundle reload (e.g. Capgo OTA) replaced our token provider but
    // the native SDK still holds its original instance, RequestedConnectionToken
    // never reaches us and the SDK silently times out after ~30s. Detect this
    // early: if we don't observe a token request within TOKEN_REQUEST_WATCHDOG_MS,
    // surface a clear error and reset `initialized` so the next attempt re-runs
    // Terminal.initialize().
    try {
        updateTapToPayProgress('connecting_reader', 'Connecting Tap to Pay on this phone…');
        recordTapToPayDiagnostic('connect', 'Connecting reader', {
            reader: summarizeReader(readers[0]),
            hasLocationId: Boolean(locationId),
            locationHint: locationHint(locationId),
        });
        const connectStartedAt = Date.now();
        const tokenRequestBaselineAt = lastTokenRequestAt;
        const progressBaselineAt = lastTerminalProgressAt;

        const connectPromise = StripeTerminal.connectReader({
            reader: readers[0],
            ...(useTapToPay && locationId ? { locationId } : {}),
            autoReconnectOnUnexpectedDisconnect: true,
        } as any);

        const watchdogPromise = new Promise<void>((_, reject) => {
            const intervalId = window.setInterval(() => {
                // Disarm as soon as the SDK shows ANY sign of life: a connection
                // token request OR a connection/payment status change. The latter
                // covers the cached-session case where no new token is requested,
                // which previously tripped the watchdog even though connect was
                // progressing normally.
                if (lastTokenRequestAt > tokenRequestBaselineAt
                    || lastTerminalProgressAt > progressBaselineAt) {
                    window.clearInterval(intervalId);
                } else if (!appIsActive) {
                    // First-run Apple onboarding / Terms sheet backgrounds the app.
                    // Don't count that user-paced time against the stall watchdog.
                } else if (Date.now() - Math.max(connectStartedAt, lastAppResumeAt) >= TOKEN_REQUEST_WATCHDOG_MS) {
                    // Only foreground time counts: the window restarts from the last
                    // resume so returning from onboarding gets a fresh full window.
                    window.clearInterval(intervalId);
                    reject(new Error(TOKEN_BRIDGE_STALLED_MESSAGE));
                }
            }, 250);
            void connectPromise.finally(() => window.clearInterval(intervalId));
        });

        await Promise.race([connectPromise, watchdogPromise]);
        connectionStatus = 'connected';
        markTapToPayOnboarded();
        recordTapToPayDiagnostic('connect', 'Reader connected', {
            durationMs: Date.now() - connectStartedAt,
        });
    } catch (err: any) {
        const raw = extractMessage(err);
        recordTapToPayDiagnostic('connect', 'Reader connection failed', {
            error: raw,
            hasLocationId: Boolean(locationId),
            locationHint: locationHint(locationId),
        }, 'error');

        // Reconcile with native truth before treating a watchdog stall or an
        // "already connected" error as a failure. Promise.race doesn't cancel the
        // native connect, so it frequently completes AFTER the watchdog fired —
        // native:connected / JS:not_connected. Trusting getConnectedReader() here
        // is what breaks the "fails first time, then keeps failing until app
        // restart" loop.
        if (raw === TOKEN_BRIDGE_STALLED_MESSAGE
            || raw.includes('Connection token bridge stalled')
            || isAlreadyConnectedError(raw)) {
            if (await hasNativeConnectedReader()) {
                markTapToPayOnboarded();
                recordTapToPayDiagnostic('connect', 'Reader already connected on reconcile; treating connect as success', {
                    trigger: raw,
                });
                updateTapToPayProgress('connecting_reader', 'Reader connected. Preparing for tap…');
                return;
            }

            if (isAlreadyConnectedError(raw)) {
                // Native claims "already connected" but getConnectedReader disagrees:
                // the connection is wedged. Force a clean disconnect so the next
                // attempt starts fresh instead of failing forever.
                await cleanupTerminal();
                throw new Error('Tap to Pay reader was in a stuck state and has been reset. Please try again.');
            }

            // Genuine bridge stall: force the next attempt to re-run
            // Terminal.initialize() so a fresh TokenProvider is wired up natively.
            initialized = false;
            connectionStatus = 'not_connected';
            throw err;
        }

        if (raw.includes('ATTESTATION_FAILURE') || raw.includes('GMS certified') || raw.includes('Google Mobile Services')) {
            throw new Error(
                'Tap to Pay connection failed because this device is not Google Play certified. ' +
                'Open the Play Store app, go to Settings -> About, and check Play Protect certification. It must say Device is certified.'
            );
        }

        if (raw.toLowerCase().includes('location') || raw.toLowerCase().includes('account')) {
            throw new Error(
                'Tap to Pay connection failed because the Terminal location does not match the Stripe account used for this payment. ' +
                'For connected accounts, the location must be created inside that connected account, not the platform or main Stripe account.'
            );
        }

        throw new Error(`Reader connection failed: ${raw}`);
    }
}

async function collectAndConfirmTapToPayPaymentImpl(clientSecret: string): Promise<void> {
    recordTapToPayDiagnostic('collect', 'Starting payment collection on connected reader', {
        hasClientSecret: Boolean(clientSecret),
    });

    // 3. Collect payment method (customer taps card / NFC device)
    try {
        updateTapToPayProgress('connecting_reader', 'Reader connected. Starting Tap to Pay…');
        recordTapToPayDiagnostic('collect', 'Collecting payment method');
        await StripeTerminal.collectPaymentMethod({ paymentIntent: clientSecret });
        updateTapToPayProgress('processing_payment', 'Card detected. Processing payment…');
        recordTapToPayDiagnostic('collect', 'Payment method collected');
    } catch (err: any) {
        const errorMessage = extractMessage(err);
        recordTapToPayDiagnostic('collect', 'Collect payment method failed', {
            error: errorMessage,
        }, 'error');

        if (isContactlessTransactionCanceledError(errorMessage) || isReaderDismissedError(errorMessage)) {
            throw stampCollectStage(new Error(normalizeCollectPaymentErrorMessage(errorMessage)));
        }

        throw stampCollectStage(err);
    }

    // 4. Confirm the PaymentIntent (sends to Stripe, triggers webhook)
    try {
        updateTapToPayProgress('confirming_payment', 'Confirming payment…');
        recordTapToPayDiagnostic('confirm', 'Confirming payment intent');
        await StripeTerminal.confirmPaymentIntent();
        recordTapToPayDiagnostic('confirm', 'Payment intent confirmed');
    } catch (err: any) {
        recordTapToPayDiagnostic('confirm', 'Confirm payment intent failed', {
            error: extractMessage(err),
        }, 'error');
        throw err;
    }
}

/**
 * Initialize the SDK, resolve the Terminal location and connect a reader —
 * idempotently, and deduped across concurrent callers so a background warm and a
 * user-initiated payment share one piece of in-flight work.
 *
 * Module-scoped rather than owned by a component: the reader connection has to
 * outlive any single modal for the warm-up to be worth anything.
 */
export function ensureReaderWarm(locationId?: string): Promise<void> {
    if (warmPromise) {
        return warmPromise;
    }

    const work = (async () => {
        await initializeTerminal();
        const resolvedLocationId = locationId
            ?? (await apiService.ensureTerminalLocation()).location_id;
        await discoverAndConnectTapToPayReaderImpl(resolvedLocationId);
    })();

    warmPromise = work.finally(() => {
        warmPromise = null;
    });
    return warmPromise;
}

/**
 * Whether the most recent warm reused an already-connected reader instead of
 * establishing a new one. A reused connection may have gone stale while idle, so
 * callers use this to decide whether a downstream failure is worth one silent
 * reconnect-and-retry before it reaches the user.
 */
export function didReuseExistingConnection(): boolean {
    return reusedExistingConnection;
}

/** Whether a reader is connected right now, according to the native SDK. */
export function isReaderConnected(): Promise<boolean> {
    return hasNativeConnectedReader();
}

/**
 * Whether location access is already granted. Checks only — never prompts — so
 * it is safe to call from the launch-time warm.
 *
 * Stripe Terminal needs location on both platforms, but neither asks for it at a
 * useful time. Android's patched plugin requests it inside `initialize()`, and
 * the iOS SDK requests it lazily when it attaches location to the card-present
 * transaction — which lands *after* the customer has tapped. So we resolve it
 * ourselves ahead of time; see requestTapToPayLocationPermission.
 */
export async function hasTapToPayLocationPermission(): Promise<boolean> {
    if (!isNative()) {
        return false;
    }

    try {
        const { location, coarseLocation } = await Geolocation.checkPermissions();
        return location === 'granted' || coarseLocation === 'granted';
    } catch {
        // Can't tell — assume not, and let the in-context warm ask for it.
        return false;
    }
}

/**
 * Ask for location access if it hasn't been decided yet.
 *
 * Call this from a payment screen, never at launch: the point is to get the
 * prompt out of the way while the user is deciding to take a payment, instead of
 * having the Stripe SDK raise it mid-tap with a card against the phone.
 *
 * Returns whether access ended up granted. A denial is not fatal here — the
 * payment flow still runs and surfaces the SDK's own error if it matters.
 */
export async function requestTapToPayLocationPermission(): Promise<boolean> {
    if (!isNative()) {
        return false;
    }

    if (await hasTapToPayLocationPermission()) {
        return true;
    }

    try {
        // Serialized against the app's other permission prompts — Android drops
        // overlapping runtime requests, and stacked iOS sheets are just as bad.
        const result = await requestPermissionExclusive(() =>
            Geolocation.requestPermissions({ permissions: ['location'] })
        );
        const granted = result.location === 'granted' || result.coarseLocation === 'granted';
        recordTapToPayDiagnostic('permission', 'Location permission requested for Tap to Pay', {
            granted,
            location: result.location,
            coarseLocation: result.coarseLocation,
        }, granted ? 'info' : 'warn');
        return granted;
    } catch (err: any) {
        recordTapToPayDiagnostic('permission', 'Location permission request failed', {
            error: extractMessage(err),
        }, 'warn');
        return false;
    }
}

/**
 * Whether Tap to Pay is enabled on this device. Gates the unprompted launch-time
 * warm (the first connect triggers Apple's onboarding / Terms sheet, which must
 * only appear in response to a user action) and whether the checkout button runs
 * setup or takes a payment.
 *
 * §1.5: acceptance status should come from Apple/the PSP, not a local variable. A
 * currently-connected reader can only exist post-onboarding, so the live SDK state
 * is authoritative and wins over the cached flag (and self-heals it). The persisted
 * flag is only a fallback for when no reader is connected — readers disconnect
 * between sessions, so "not connected" is not proof of "not onboarded". The read is
 * connection-only; it never triggers a connect or Apple's sheet.
 */
export async function hasCompletedTapToPayOnboarding(): Promise<boolean> {
    try {
        if (await isReaderConnected()) {
            markTapToPayOnboarded();
            return true;
        }
    } catch {
        // Fall through to the cached flag.
    }

    if (onboardedCache != null) {
        return onboardedCache;
    }

    try {
        onboardedCache = (await getStorageItem(TAP_TO_PAY_ONBOARDED_STORAGE_KEY)) === 'true';
    } catch {
        onboardedCache = false;
    }

    return onboardedCache;
}

function markTapToPayOnboarded(): void {
    if (onboardedCache) {
        return;
    }
    onboardedCache = true;
    void setStorageItem(TAP_TO_PAY_ONBOARDED_STORAGE_KEY, 'true').catch(() => {});
}

export async function collectAndConfirmTapToPayPayment(clientSecret: string): Promise<void> {
    await collectAndConfirmTapToPayPaymentImpl(clientSecret);
}

/**
 * Cancel an in-progress collectPaymentMethod call.
 * Safe to call even if no collection is in progress.
 */
export async function cancelCollect(): Promise<void> {
    // Drop any pending intent mark so an abandoned attempt can't be measured
    // against the next one's ready event.
    readyMarkAt = null;

    try {
        // Bounded like every other native housekeeping call. Observed on iOS:
        // cancelCollectPaymentMethod never resolves when it is called before the
        // native collect is properly under way (cancel pressed moments after the
        // sheet opens). An unbounded await here hung the caller, which left the
        // payment state machine wedged mid-flow — see `cancel` in useTapToPay.
        await withTimeout(
            StripeTerminal.cancelCollectPaymentMethod(),
            NATIVE_HOUSEKEEPING_TIMEOUT_MS
        );
        recordTapToPayDiagnostic('cancel', 'Collect payment method canceled');
    } catch (err) {
        // The SDK throws if nothing is in progress, and a timeout means the native
        // call never came back. Neither is actionable, but both are worth seeing in
        // diagnostics — the silence here is what made the wedge hard to spot.
        recordTapToPayDiagnostic('cancel', 'Collect cancel did not complete cleanly', {
            error: extractMessage(err),
        }, 'warn');
    }

    // Deliberately does NOT cancel discovery. Discovery now belongs to the
    // module-scoped warm, not to whichever screen happens to be mounted, and a
    // warm frequently outlives the component that triggered it — TakePaymentModal
    // re-keys ChargeCardModal when the charge sheet opens, so the hook unmounts
    // mid-warm and used to kill the in-flight discovery ("discoverReaders was
    // canceled", then a full 15s timeout on the first payment after install).
    //
    // Finding G (a stale discovery blocking the next attempt) stays covered:
    // discoverAndConnectTapToPayReaderImpl cancels before it starts a new
    // discovery, and cleanupTerminal cancels on teardown.
}

/**
 * Disconnect the reader and clean up module state.
 * Call this when navigating away from the payment screen.
 *
 * Guarded against the noisy "no reader to disconnect" path: if our local
 * connection mirror says we are not connected, skip the native call.
 */
export async function cleanupTerminal(): Promise<void> {
    // Nothing was ever set up in this session and nothing is connected: bail
    // before touching any native Terminal method. getConnectedReader() (and other
    // Terminal.getInstance() calls) throw before initialize(), and the hook's
    // unmount cleanup runs on every Charge Card open/close — including the very
    // first one, long before the SDK is initialized.
    if (!initialized && connectionStatus === 'not_connected') {
        return;
    }

    // Cancel any in-flight discovery first so it doesn't poison the next session (G).
    // Bounded: after a FAILED discovery the native cancelable's callback may never
    // fire, and an unbounded await here would hang the failed-state transition.
    try {
        await withTimeout(StripeTerminal.cancelDiscoverReaders(), NATIVE_HOUSEKEEPING_TIMEOUT_MS);
    } catch {
        // Nothing to cancel, or the cancel stalled — ignore either way.
    }

    // Trust native truth over the JS mirror (F). After a webview reload the mirror
    // can read 'not_connected' while a reader is still connected natively; the old
    // guard then skipped the disconnect and left the session unrecoverable.
    if (!(await hasNativeConnectedReader())) {
        connectionStatus = 'not_connected';
        return;
    }

    try {
        await withTimeout(StripeTerminal.disconnectReader(), NATIVE_HOUSEKEEPING_TIMEOUT_MS);
        connectionStatus = 'not_connected';
        recordTapToPayDiagnostic('cleanup', 'Reader disconnected during cleanup');
    } catch {
        // Ignore disconnect errors / timeout
    }
}

/**
 * Errors that indicate the reader is in an unrecoverable state and the next
 * attempt must start from a fresh disconnect. Transient errors (e.g. the
 * customer canceled the tap, the card read timed out) should NOT trigger a
 * disconnect — keeping the connection alive lets the user retry instantly.
 */
/**
 * Tag an error as having come from `collectPaymentMethod` rather than
 * `confirmPaymentIntent`. Callers use this to decide whether replaying the same
 * PaymentIntent is safe — after a confirm failure it is not.
 */
function stampCollectStage<T>(err: T): T {
    if (err && typeof err === 'object') {
        (err as any).tapToPayStage = 'collect';
    }
    return err;
}

export function isCollectStageError(err: unknown): boolean {
    return Boolean(err && typeof err === 'object' && (err as any).tapToPayStage === 'collect');
}

/**
 * Failures caused by how the card was presented, or by the user backing out —
 * not by the reader. These leave the connection healthy, so a retry should not
 * pay for a reconnect, and nothing should silently re-arm the reader.
 *
 * Cancellation matters most here. On iOS `collectPaymentMethod` presents Apple's
 * own Tap to Pay sheet, and dismissing *that* fails the collect with "The
 * command was canceled" without any JS cancel path running — so this predicate
 * is the only thing that can tell a deliberate stop from a broken reader.
 */
export function isTransientTapToPayError(message: string | null | undefined): boolean {
    if (!message) return false;
    const lower = message.toLowerCase();
    return (
        // Matched broadly and deliberately: the safe direction is to treat an
        // unrecognized cancellation as intentional and stop, never to re-present
        // the reader at someone who just dismissed it.
        lower.includes('cancel')
        || lower.includes('card was not read')
        || lower.includes('try another card')
        || lower.includes('multiple contactless cards')
        || lower.includes('card read')
    );
}

export function isFatalTapToPayError(message: string | null | undefined): boolean {
    if (!message) return false;
    const lower = message.toLowerCase();
    return (
        lower.includes('attestation_failure')
        || lower.includes('gms certified')
        || lower.includes('google mobile services')
        || lower.includes('developer options')
        || lower.includes('connection token bridge stalled')
        || lower.includes('not google play certified')
    );
}

export { recordTapToPayDiagnostic };
